// Trips API client — Phase 4 Plan 04-03 (DIS-07, DIS-16).
//
// Wraps:
//   POST /api/v1/trips             (create multi-stop trip + recipients)
//   GET  /api/v1/trips             (paginated list)
//   GET  /api/v1/trips/{trip_id}   (full dispatcher view)
//
// Schemas mirror apps/api/src/silentdispatcher/api/schemas/trip.py.

import type { ApiClient } from "./client";

export type StopRole = "pickup" | "dropoff" | "intermediate";
export type TripStatus = string;

export interface StopCreate {
  address: string;
  lat: number;
  lng: number;
  formatted_address?: string | null;
  cargo_description?: string | null;
  planned_arrival_window_start?: string | null;
  planned_arrival_window_end?: string | null;
  geofence_radius_m?: number | null;
  // Echo-only — server re-assigns by position.
  role?: StopRole | null;
}

export interface RecipientCreate {
  recipient_email: string;
  recipient_locale: "de" | "en" | "fr";
  recipient_telegram_username?: string | null;
  segment_filter_stop_ids?: number[];
}

export interface TripCreatePayload {
  driver_id?: number | null;
  external_id?: string | null;
  stops: StopCreate[];
  recipients: RecipientCreate[];
  notify_recipients: boolean;
}

export interface TripTrackerUrl {
  invite_id: number;
  recipient_email: string;
  tracker_url: string;
}

export interface TripCreateResponse {
  trip_id: number;
  stop_count: number;
  recipient_count: number;
  tracker_urls: TripTrackerUrl[];
}

export interface TripListItem {
  id: number;
  status: TripStatus;
  gps_status: string | null;
  driver_id: number | null;
  external_id: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface TripListResponse {
  items: TripListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface StopResponse {
  id: number;
  sequence_idx: number;
  role: StopRole;
  address: string | null;
  geofence_radius_m: number;
  arrived_at: string | null;
  departed_at: string | null;
}

export interface TripEventItem {
  id: number;
  event_type: string;
  occurred_at: string;
  payload: Record<string, unknown>;
  stop_point_id: number | null;
  created_by_user_id: number | null;
}

export interface TripDetailsResponse {
  id: number;
  status: TripStatus;
  gps_status: string | null;
  driver_id: number | null;
  external_id: string | null;
  customer_email: string | null;
  visibility_settings: Record<string, boolean>;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  stops: StopResponse[];
  events: TripEventItem[];
}

export interface TripListFilters {
  status?: "active" | "completed" | "problems" | "all";
  driver_id?: number;
  search?: string;
  offset?: number;
  limit?: number;
}

function _buildQuery(filters: TripListFilters): string {
  const p = new URLSearchParams();
  if (filters.status) p.set("status", filters.status);
  if (filters.driver_id !== undefined) p.set("driver_id", String(filters.driver_id));
  if (filters.search !== undefined && filters.search.length > 0) p.set("search", filters.search);
  if (filters.offset !== undefined) p.set("offset", String(filters.offset));
  if (filters.limit !== undefined) p.set("limit", String(filters.limit));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const tripsApi = {
  create(client: ApiClient, payload: TripCreatePayload): Promise<TripCreateResponse> {
    return client.post<TripCreateResponse>("/api/v1/trips", payload);
  },

  list(client: ApiClient, filters: TripListFilters = {}): Promise<TripListResponse> {
    return client.get<TripListResponse>(`/api/v1/trips${_buildQuery(filters)}`);
  },

  details(client: ApiClient, tripId: number): Promise<TripDetailsResponse> {
    return client.get<TripDetailsResponse>(`/api/v1/trips/${tripId}`);
  },
};
